import React from 'react';
import clsx from 'clsx';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { useAuthUser } from '@/lib/hooks/useAuthUser';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { mutate } from 'swr';

import { useI18n } from '@/i18n/I18nProvider';
import { authenticatedFetcher } from '@/lib/swr';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';
import { useGetStudentById } from '@/lib/hooks/useGetStudentById';
import { useGetStudents } from '@/lib/hooks/useGetStudents';
import { updateStudentStatus } from '@/lib/hooks/useUpdateStudentStatus';
import type { StudentAddress, StudentProfileV1, StudentProfileWithMeta } from '@/types/studentprofile';
import type { IDHStudent } from '@/types/idh';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import { AddressPicker } from '@/components/forms/AddressPicker';
import type { AddressPickerProps } from '@/components/forms/AddressPicker';
import { isDubaiOrAbuDhabi } from '@/components/forms/addressTypes';
import { estimateBytesFromBase64, formatBytes, formatCoordinate, textOrNull } from '@/lib/helpers';
import { validateUAEMobile } from '@/lib/validators/uae-mobile';
import Spinner from '@/components/ui/spinner';
import { resolveLatestEnrollment } from '@/pages/dashboard/components/StudentCard';
import { useGetStudentIdhInfo, fetchStudentIdhInfo } from '@/lib/hooks/useGetStudentIdhInfo';
import { uploadAttachment } from '@/lib/upload-attachment';
import { StatusCard } from '@/components/StatusCard';
import { PP_BASE_URL, FILE_SERVER_URL } from '@/lib/env';
import { CaretLeftIcon, CaretRightIcon, CheckCircleIcon } from '@/components/icons';

function formatAddress(address?: StudentAddress | null): string {
  if (!address) return '';
  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3,
    address.plotNumber,
    address.roadNumber,
    address.sector,
    address.city,
    address.state,
    address.country,
    address.poBox ? `PO Box ${address.poBox}` : null,
    address.zipCode,
  ];
  return parts
    .map((part) => (typeof part === 'string' ? part.trim() : part))
    .filter((part) => part && String(part).length > 0)
    .join(', ');
}

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ATTACHMENT_LIMIT_LABEL = '5 MB';
const ALLOWED_ATTACHMENT_TYPES = ['application/pdf'];
const PRIMARY_PHONE_FROM_UAEPASS = true;
const ALLOW_CHANGING_PRIMARY_PHONE = true;
const ALLOW_CHANGING_TRANSPORT_MODE = false;

// Normalize a free-text transportation value from IDH into our select model
function normalizeTransportation(raw?: string | null): { value: 'Bus' | 'Car' | 'On Foot' } {
  const t = (raw ?? '').trim();
  if (!t) return { value: 'On Foot' };
  const l = t.toLowerCase();
  const isCar = l === 'car' || l === 'private car' || l === 'private' || l === 'بالسيارة';
  const isBus = l === 'bus' || l === 'school bus' || l === 'schoolbus' || l === 'بواسطة الحافلة';
  const isOnFoot =
    l === 'on foot' || l === 'walking' || l === 'walk' || l === 'سيراً على الأقدام' || l === 'سيرا على الاقدام';
  if (isCar) return { value: 'Car' };
  if (isBus) return { value: 'Bus' };
  if (isOnFoot) return { value: 'On Foot' };
  return { value: 'On Foot' };
}

function getDisplayName(profile: StudentProfileV1, locale: string): string {
  const arabic = [profile.firstNameArabic, profile.middleNameArabic, profile.lastNameArabic].filter(Boolean).join(' ');
  const english = [
    profile.firstNameEnglish,
    profile.middleNameEnglish,
    profile.thirdNameEnglish,
    profile.fourthNameEnglish,
    profile.familyNameEnglish,
  ]
    .filter(Boolean)
    .join(' ');
  const fallback = profile.id ?? '';
  return locale === 'ar' ? arabic || english || fallback : english || arabic || fallback;
}

// Helper: submit payload to IDH and normalize error shape
async function submitToIDH(idhPayload: IDHStudent): Promise<void> {
  const ppBaseUrl = PP_BASE_URL;
  await authenticatedFetcher(`${ppBaseUrl}/students/${encodeURIComponent(idhPayload.sourceId)}/info`, {
    method: 'POST',
    body: JSON.stringify(idhPayload),
  });
}

type AddressValue = NonNullable<AddressPickerProps['value']> & {
  emirateName?: string | null;
  areaName?: string | null;
  communityName?: string | null;
};
type Mode = 'init' | 'edit';

type PreparedPayload = {
  studentId: string;
  mode: Mode;
  contactNumbers: string[];
  addressChanged: boolean;
  newAddress?: AddressValue | null;
  documentName?: string | null;
  transportation: string;
  changedByParent?: boolean;
};

type EligibleChild = {
  profile: StudentProfileV1;
  idhStatusId: number | null;
  educationType: string | null;
};

export default function UpdateStudentInfoPage() {
  const { t, locale } = useI18n();
  const toast = useToastNotifications();
  const { emiratesId: userEmiratesId, phoneNumber: userPhoneNumber } = useAuthUser();
  const { students } = useGetStudents();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sourcedId = params?.id as string;
  const mode: Mode = searchParams?.get('mode') == 'resubmit' ? 'edit' : 'init';

  const getPrefilledContacts = (
    mode: Mode,
    sessionPhone: string | null | undefined,
    primaryPhone: string | null | undefined,
    otherPhone: string | null | undefined
  ): string[] => {
    const numbers: string[] = [];
    const sessionTrimmed = (sessionPhone ?? '').trim();
    const primaryTrimmed = (primaryPhone ?? '').trim();
    const primary = mode === 'init' ? (PRIMARY_PHONE_FROM_UAEPASS ? sessionTrimmed : primaryTrimmed) : primaryTrimmed;
    if (primary) numbers.push(primary);

    const otherTrimmed = (otherPhone ?? '').trim();
    if (otherTrimmed) numbers.push(otherTrimmed);

    return numbers.length > 0 ? numbers.slice(0, 2) : [''];
  };

  // ========== MODE-SPECIFIC DATA FETCHING ==========
  // Student data is always needed (for display name, enrollment, etc.)
  const { student, error: studentError, isLoading: studentLoading } = useGetStudentById(sourcedId);

  // Check previous application status
  const {
    idhResponse: idhResp,
    error: idhError,
    isLoading: idhLoading,
  } = useGetStudentIdhInfo(sourcedId, {
    enabled: true,
  });

  // Resolve loading and error states
  const isLoading = studentLoading || idhLoading;

  const meta = (student as StudentProfileWithMeta | undefined)?.meta;
  const primaryAddress = React.useMemo<StudentAddress | null>(() => {
    if (!student?.addresses || student.addresses.length === 0) return null;
    // Find address with isPrimary flag, fallback to first address
    const primary = student.addresses.find((addr) => addr.isPrimary);
    const selected = primary ?? student.addresses[0];
    return selected;
  }, [student]);

  const formattedCurrentAddress = React.useMemo(() => formatAddress(primaryAddress), [primaryAddress]);
  // Format previously submitted address from IDH (edit mode only)
  const formattedPrevSubmittedAddress = React.useMemo(() => {
    const idh = idhResp?.data;
    if (!idh) return '';
    const parts = [
      textOrNull(idh.houseBuilding),
      textOrNull(idh.street),
      textOrNull(idh.plot),
      textOrNull(idh.mainPlot),
      textOrNull(idh.premises),
      textOrNull(idh.area),
      textOrNull(idh.zone),
      textOrNull(idh.region),
      textOrNull(idh.emirate),
    ].filter(Boolean) as string[];
    return parts.join(', ');
  }, [idhResp]);

  const [contactNumbers, setContactNumbers] = React.useState<string[]>(['']);
  const contactsInitialized = React.useRef(false);
  const [addressChanged, setAddressChanged] = React.useState<boolean>(false);
  const [newAddress, setNewAddress] = React.useState<AddressValue | null>(null);
  const [supportingDocument, setSupportingDocument] = React.useState<File | null>(null);
  const [transportation, setTransportation] = React.useState<string>('');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [, setAddressSaveState] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const addressSignatureRef = React.useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isFetchingManhalCodes, setIsFetchingManhalCodes] = React.useState<boolean>(false);
  const [showSuccessToast, setShowSuccessToast] = React.useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState<boolean>(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const firstErrorRef = React.useRef<HTMLDivElement>(null);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);
  const [preparedPayload, setPreparedPayload] = React.useState<PreparedPayload | null>(null);
  const [idhAttachmentUrl, setIdhAttachmentUrl] = React.useState<string | null>(null);
  const [eligibleChildren, setEligibleChildren] = React.useState<EligibleChild[]>([]);
  const [selectedChildIds, setSelectedChildIds] = React.useState<string[]>([]);
  const [applyToAllChildren, setApplyToAllChildren] = React.useState<boolean>(false);
  const [isFetchingChildren, setIsFetchingChildren] = React.useState<boolean>(false);
  const [childFetchError, setChildFetchError] = React.useState<string | null>(null);
  const idhAttachmentSize = React.useMemo(
    () => estimateBytesFromBase64(idhResp?.data?.attachment01 ?? null),
    [idhResp]
  );
  const eligibleChildIds = React.useMemo(
    () =>
      eligibleChildren
        .map((entry) => entry.profile.id)
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0),
    [eligibleChildren]
  );

  // ========== INIT MODE: Initialize form from OneRoster data ==========
  React.useEffect(() => {
    if (mode !== 'init') return;
    if (!student || contactsInitialized.current) return;
    const sessionPhone = userPhoneNumber;
    let mobileContacts = (student.contacts || [])
      .filter((contact) => contact.type === 'Mobile' && contact.value)
      .sort((a, b) => {
        // Sort by isPrimary flag: primary contacts first
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        return 0;
      })
      .map((contact) => contact.value.trim())
      .slice(0, 2);

    mobileContacts = getPrefilledContacts(mode, sessionPhone, mobileContacts.at(0), mobileContacts.at(1));
    setContactNumbers(mobileContacts.length > 0 ? mobileContacts : ['']);

    if (student.transportationType?.trim()) {
      const { value } = normalizeTransportation(student.transportationType);
      setTransportation(value);
    }

    contactsInitialized.current = true;
  }, [student, mode, userPhoneNumber]);

  // ========== EDIT MODE: Initialize form from IDH data ==========
  React.useEffect(() => {
    if (mode !== 'edit') return;

    const idh = idhResp?.data;
    if (!idh) return;

    // Prefill contact numbers
    const sessionPhone = userPhoneNumber;
    const contacts = getPrefilledContacts(mode, sessionPhone, idh.primaryPhone, idh.otherPhone);
    setContactNumbers(contacts);

    // Prefill transportation
    const { value } = normalizeTransportation(idh.transportationType);
    setTransportation(value);
  }, [mode, idhResp, userPhoneNumber]);

  // ========== Track unsaved changes (mode-specific baseline) ==========
  React.useEffect(() => {
    let initialContacts: string[] = [];
    let initialTransportation = '';
    const sessionPhone = userPhoneNumber;

    if (mode === 'edit') {
      // EDIT MODE: baseline from session (if toggle enabled) or IDH data
      const idh = idhResp?.data;
      initialContacts = getPrefilledContacts(mode, sessionPhone, idh?.primaryPhone, idh?.otherPhone);
      initialTransportation = (idh?.transportationType ?? '').trim();
    } else {
      // INIT MODE: baseline from OneRoster data
      initialContacts = (student?.contacts || [])
        .filter((contact) => contact.type === 'Mobile' && contact.value)
        .sort((a, b) => {
          // Sort by isPrimary flag: primary contacts first
          if (a.isPrimary && !b.isPrimary) return -1;
          if (!a.isPrimary && b.isPrimary) return 1;
          return 0;
        })
        .map((contact) => contact.value.trim())
        .slice(0, 2);
      initialContacts = getPrefilledContacts(mode, sessionPhone, initialContacts.at(0), initialContacts.at(1));
      initialTransportation = student?.transportationType?.trim()
        ? normalizeTransportation(student.transportationType).value
        : '';
    }

    const baseline = initialContacts.length > 0 ? initialContacts : [''];
    const contactsChanged = JSON.stringify(contactNumbers) !== JSON.stringify(baseline);
    const transportationChanged = transportation !== initialTransportation;
    const hasChanges = contactsChanged || addressChanged || transportationChanged;
    setHasUnsavedChanges(hasChanges);
  }, [contactNumbers, addressChanged, transportation, student, mode, idhResp, userPhoneNumber]);

  // Warn before leaving with unsaved changes
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !isSubmitting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isSubmitting]);

  // Scroll to first error on validation failure
  React.useEffect(() => {
    if (errorMessage && firstErrorRef.current) {
      firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstErrorRef.current.focus();
    }
  }, [errorMessage]);

  React.useEffect(() => {
    if (!addressChanged) {
      setNewAddress(null);
      setSupportingDocument(null);
    }
  }, [addressChanged]);

  React.useEffect(() => {
    if (!showConfirmDialog) {
      setApplyToAllChildren(false);
      setSelectedChildIds([]);
      setEligibleChildren([]);
      setChildFetchError(null);
      setIsFetchingChildren(false);
    }
  }, [showConfirmDialog]);

  React.useEffect(() => {
    setSelectedChildIds((prev) => {
      if (prev.length === 0) return prev;
      const valid = prev.filter((id) => eligibleChildIds.includes(id));
      return valid.length === prev.length ? prev : valid;
    });
  }, [eligibleChildIds]);

  // ========== Build PDF attachment URL (EDIT MODE only) ==========
  React.useEffect(() => {
    if (mode !== 'edit') return;

    const fileGuid = (idhResp?.data?.attachment01 ?? '').trim();
    if (fileGuid) {
      // Use file-share API download endpoint with FileGuid from environment variable
      const fileServerUrl = FILE_SERVER_URL;
      const downloadUrl = `${fileServerUrl}/Download/Stream/${fileGuid}`;
      setIdhAttachmentUrl(downloadUrl);
    } else {
      setIdhAttachmentUrl(null);
    }
  }, [mode, idhResp?.data?.attachment01]);

  React.useEffect(() => {
    if (!addressChanged) {
      setAddressSaveState('idle');
      addressSignatureRef.current = null;
      return;
    }

    if (!newAddress || !newAddress.emirateId || !newAddress.areaId) {
      setAddressSaveState('idle');
      return;
    }

    const hasPlot = typeof newAddress.plotId === 'number' && Number.isFinite(newAddress.plotId);
    const hasStreetDetails = Boolean((newAddress.streetName ?? '').trim() && (newAddress.houseNumber ?? '').trim());

    if (!hasPlot && !hasStreetDetails) {
      setAddressSaveState('idle');
      return;
    }

    const signature = JSON.stringify(newAddress);
    if (signature === addressSignatureRef.current) {
      setAddressSaveState('saved');
      return;
    }

    let cancelled = false;
    setAddressSaveState('saving');

    (async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 400));
        if (cancelled) return;
        addressSignatureRef.current = signature;
        setAddressSaveState('saved');
      } catch (autoSaveError) {
        console.error('Auto-save address failed:', autoSaveError);
        if (cancelled) return;
        setAddressSaveState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addressChanged, newAddress]);

  const updateInfo = t?.child?.updateInfo;
  const summaryFields = updateInfo?.addressSection?.summaryFields;
  // const summaryPlaceholder = updateInfo?.addressSection?.summaryPending;

  // Debug logging
  React.useEffect(() => {
    console.log('🔍 Debug Info:', {
      't exists': !!t,
      locale: locale,
      'updateInfo exists': !!updateInfo,
      'updateInfo type': typeof updateInfo,
      'summaryFields exists': !!summaryFields,
    });
  }, [t, locale, updateInfo, summaryFields]);

  const handleContactChange = (index: number, value: string) => {
    setContactNumbers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const getContactNumberError = (number: string, index: number): string | null => {
    if (!number.trim()) return null;
    const validation = validateUAEMobile(number);
    if (!validation.isValid) {
      return locale === 'ar' ? 'يجب أن يبدأ الرقم بـ 05 (10 أرقام)' : validation.error || 'Invalid format';
    }
    return null;
  };

  const handleAddContact = () => {
    setContactNumbers((prev) => (prev.length < 2 ? [...prev, ''] : prev));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (file) {
      if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
        setErrorMessage(locale === 'ar' ? 'يجب أن يكون المستند بصيغة PDF.' : 'Attachment must be a PDF file.');
        event.target.value = '';
        setSupportingDocument(null);
        return;
      }

      if (file.size > MAX_ATTACHMENT_BYTES) {
        setErrorMessage(
          locale === 'ar'
            ? `حجم الملف المرفق كبير جداً. الحد الأقصى المسموح هو ${ATTACHMENT_LIMIT_LABEL}.`
            : `Attachment is too large. Maximum allowed size is ${ATTACHMENT_LIMIT_LABEL}.`
        );
        event.target.value = '';
        setSupportingDocument(null);
        return;
      }
    }

    setSupportingDocument(file);
  };

  // ========== Helper: Check if address is Abu Dhabi (used for document requirement logic) ==========
  const isAbuDhabiAddress = (address?: AddressValue | null): boolean => {
    return !!(
      address?.emirateNameEn &&
      (address.emirateNameEn.toLowerCase().includes('abu dhabi') ||
        address.emirateNameEn.toLowerCase().includes('al ain') ||
        address.emirateNameEn.toLowerCase().includes('dhafra'))
    );
  };

  // ========== Compute confirmation address (mode-specific fallback) ==========
  const confirmAddress = React.useMemo(() => {
    type FlatAddress = {
      emirate: string;
      city: string;
      region: string;
      area: string;
      street: string;
      houseBuilding: string;
      zone: string;
      plot: string;
      mainPlot: string;
      premises: string;
      latitude: string;
      longitude: string;
    };
    const empty: FlatAddress = {
      emirate: '',
      city: '',
      region: '',
      area: '',
      street: '',
      houseBuilding: '',
      zone: '',
      plot: '',
      mainPlot: '',
      premises: '',
      latitude: '',
      longitude: '',
    };

    // If user changed address, use the new one
    if (preparedPayload?.addressChanged && preparedPayload.newAddress) {
      const next = preparedPayload.newAddress;
      // Detect if Abu Dhabi (from map) or Dubai/Northern (from dropdowns)
      const hasMapData = !!(next.municipalityNameEn || next.municipalityNameAr);

      return {
        source: 'new' as const,
        data: {
          emirate: textOrNull(next.emirateNameEn) ?? textOrNull(next.emirateName) ?? '',
          // City: Municipality for Abu Dhabi (map), Region name for Dubai/Northern (dropdown)
          city: hasMapData ? (textOrNull(next.municipalityNameEn) ?? '') : (textOrNull(next.regionNameEn) ?? ''),
          // Region: District for Abu Dhabi (map), Zone name for Dubai/Northern (dropdown)
          region: hasMapData ? (textOrNull(next.zoneNameEn) ?? '') : (textOrNull(next.zoneNameEn) ?? ''),
          // Area: Community for Abu Dhabi (map), Area name for Dubai/Northern (dropdown)
          area: textOrNull(next.areaNameEn) ?? textOrNull(next.areaName) ?? '',
          street: textOrNull(next.fullAddressEn) ?? textOrNull(next.fullAddressAr) ?? textOrNull(next.streetName) ?? '',
          houseBuilding: textOrNull(next.houseNumber) ?? '',
          zone: textOrNull(next.zoneNameEn) ?? '',
          plot: next.plotId?.toString() ?? '',
          mainPlot: textOrNull(next.mainPlotId) ?? '',
          premises: textOrNull(next.premisesPlotId) ?? textOrNull(next.communityName) ?? '',
          latitude: formatCoordinate(next.latitude) ?? '',
          longitude: formatCoordinate(next.longitude) ?? '',
        } satisfies FlatAddress,
      };
    }

    // EDIT MODE: fallback to previously submitted IDH address
    if (mode === 'edit' && idhResp?.data) {
      const prev = idhResp.data;
      return {
        source: 'idh' as const,
        data: {
          emirate: textOrNull(prev.emirate) ?? '',
          city: '', // IDH doesn't store city separately
          region: textOrNull(prev.region) ?? '',
          area: textOrNull(prev.area) ?? '',
          street: textOrNull(prev.street) ?? '',
          houseBuilding: textOrNull(prev.houseBuilding) ?? '',
          zone: textOrNull(prev.zone) ?? '',
          plot: textOrNull(prev.plot) ?? '',
          mainPlot: textOrNull(prev.mainPlot) ?? '',
          premises: textOrNull(prev.premises) ?? '',
          latitude: textOrNull(prev.latitude) ?? '',
          longitude: textOrNull(prev.longitude) ?? '',
        } satisfies FlatAddress,
      };
    }

    // INIT MODE: fallback to OneRoster address
    if (primaryAddress) {
      return {
        source: 'oneroster' as const,
        data: {
          emirate: textOrNull(primaryAddress.state) ?? '',
          city: textOrNull(primaryAddress.region) ?? '',
          region: textOrNull(primaryAddress.sector) ?? '',
          area: textOrNull(primaryAddress.city) ?? '',
          street: textOrNull(primaryAddress.addressLine1) ?? '',
          houseBuilding: textOrNull(primaryAddress.addressLine2) ?? '',
          zone: textOrNull(primaryAddress.sector) ?? '',
          plot: textOrNull(primaryAddress.plotNumber) ?? '',
          mainPlot: textOrNull(primaryAddress.plotId) ?? '',
          premises: textOrNull(primaryAddress.addressLine3) ?? '',
          latitude: '',
          longitude: '',
        } satisfies FlatAddress,
      };
    }

    return { source: 'empty' as const, data: empty };
  }, [preparedPayload, mode, idhResp, primaryAddress]);

  const fetchEligibleChildren = React.useCallback(async (): Promise<EligibleChild[]> => {
    if (!userEmiratesId || !sourcedId) {
      setEligibleChildren([]);
      setChildFetchError(null);
      return [];
    }

    setIsFetchingChildren(true);
    setChildFetchError(null);

    try {
      const children = students;

      const otherChildren = children.filter((child) => child.id && child.id !== sourcedId);
      if (otherChildren.length === 0) {
        setEligibleChildren([]);
        return [];
      }

      const eligibilityResults = await Promise.all(
        otherChildren.map(async (child) => {
          let idhStatusId: number | null = null;
          if (child.id) {
            try {
              const idhRes = await fetchStudentIdhInfo(child.id);
              idhStatusId = idhRes?.data?.statusId ?? null;
            } catch {
              idhStatusId = null;
            }
          }
          return {
            child,
            eligible: Boolean(child.isActive),
            idhStatusId,
            educationType: resolveLatestEnrollment(child.enrollment)?.educationType ?? null,
          };
        })
      );

      const eligibleList = eligibilityResults
        .filter((result) => result.eligible && result.child.id)
        .map<EligibleChild>((result) => ({
          profile: result.child,
          idhStatusId: result.idhStatusId,
          educationType: result.educationType,
        }));

      setEligibleChildren(eligibleList);
      return eligibleList;
    } catch (error) {
      const message =
        locale === 'ar'
          ? 'تعذر التحقق من الأطفال الآخرين حالياً. يمكنك متابعة الطلب لطفل واحد.'
          : 'Unable to check other children right now. You can continue with a single-child submission.';
      setChildFetchError(message);
      setEligibleChildren([]);
      return [];
    } finally {
      setIsFetchingChildren(false);
    }
  }, [locale, userEmiratesId, sourcedId, students]);

  const handleChildSelectionChange = React.useCallback((childId: string, checked: boolean) => {
    setSelectedChildIds((prev) => {
      if (checked) {
        return prev.includes(childId) ? prev : [...prev, childId];
      }
      return prev.filter((id) => id !== childId);
    });
  }, []);

  // ========== Centralized helper: Determine changedByParent flag based on mode ==========
  const getChangedByParentFlag = (): boolean => {
    if (mode === 'init') {
      // INIT MODE: Return true only if parent made unsaved changes to the initial data
      return hasUnsavedChanges;
    } else {
      // EDIT MODE: Return whatever value was persisted from the backend
      return idhResp?.data?.changedByParent ?? false;
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    const sanitizedContacts = contactNumbers
      .map((number) => number.trim())
      .filter((value, index) => (index === 0 ? value.length > 0 : value.length > 0));

    if (!sanitizedContacts[0]) {
      setErrorMessage(updateInfo.validation.primaryContact);
      return;
    }

    // Validate primary contact number format
    const primaryValidation = validateUAEMobile(sanitizedContacts[0]);
    if (!primaryValidation.isValid) {
      setErrorMessage(
        locale === 'ar'
          ? `رقم الهاتف الأساسي غير صحيح: يجب أن يبدأ الرقم بـ 05 (10 أرقام)`
          : `Invalid primary contact number: ${primaryValidation.error}`
      );
      return;
    }

    // Validate additional contact number if provided
    if (sanitizedContacts[1]) {
      const secondaryValidation = validateUAEMobile(sanitizedContacts[1]);
      if (!secondaryValidation.isValid) {
        setErrorMessage(
          locale === 'ar'
            ? `رقم الهاتف الإضافي غير صحيح: يجب أن يبدأ الرقم بـ 05 (10 أرقام)`
            : `Invalid additional contact number: ${secondaryValidation.error}`
        );
        return;
      }
    }

    // Address validation: different rules for Abu Dhabi (map-based) vs other emirates (form-based)
    if (addressChanged) {
      if (!newAddress) {
        setErrorMessage(updateInfo.validation.addressDetails);
        return;
      }

      const isAbuDhabi = isAbuDhabiAddress(newAddress);

      if (isAbuDhabi) {
        // Abu Dhabi: must have emirate name and coordinates (from map pin)
        if (
          !newAddress.emirateNameEn ||
          newAddress.latitude === undefined ||
          newAddress.latitude === null ||
          newAddress.longitude === undefined ||
          newAddress.longitude === null
        ) {
          setErrorMessage(updateInfo.validation.addressDetails);
          return;
        }
      } else {
        // Dubai/Northern Emirates: must have emirateId and areaId
        if (
          newAddress.emirateId === undefined ||
          newAddress.emirateId === null ||
          newAddress.areaId === undefined ||
          newAddress.areaId === null
        ) {
          setErrorMessage(updateInfo.validation.addressDetails);
          return;
        }
      }
    }

    // Check if Abu Dhabi address - no document required for Abu Dhabi
    const isAbuDhabiForDoc = addressChanged && isAbuDhabiAddress(newAddress);

    if (addressChanged && !supportingDocument && !isAbuDhabiForDoc) {
      setErrorMessage(updateInfo.validation.document);
      return;
    }


    // ========== Fetch ManhalCodes for Abu Dhabi addresses BEFORE showing confirmation ==========
    let enrichedAddress = addressChanged ? newAddress : null;

    if (addressChanged && newAddress) {
      if (isAbuDhabiAddress(newAddress)) {
        // Fetch ManhalCodes from plots API using GISID
        const gisid =
          newAddress.mainPlotId || newAddress.premisesPlotId || (newAddress.plotId ? String(newAddress.plotId) : null);

        if (gisid) {
          setIsFetchingManhalCodes(true);
          try {
            const stateCode = newAddress.emirateManhalCode || 'AE.001';
            const ppBaseUrl = PP_BASE_URL;
            const plotsUrl = `${ppBaseUrl}/reference/plots?filter=${encodeURIComponent(gisid)}&stateCode=${encodeURIComponent(stateCode)}`;
            const plotsData = await authenticatedFetcher<{ data: any }>(plotsUrl);

            if (plotsData && plotsData.data) {
              // Create enriched address with ManhalCodes
              enrichedAddress = {
                ...newAddress,
                emirateManhalCode: plotsData.data.emirateManhalCode || undefined,
                regionManhalCode: plotsData.data.regionManhalCode || undefined,
                zoneManhalCode: plotsData.data.zoneManhalCode || undefined,
                areaManhalCode: plotsData.data.areaManhalCode || undefined,
              };
            }
          } catch (error) {
            console.warn('Failed to fetch ManhalCodes for preview:', error);
            // Continue with original address if fetch fails
          } finally {
            setIsFetchingManhalCodes(false);
          }
        }
      }
    }

    // Prepare payload and show confirmation dialog
    const payload: PreparedPayload = {
      studentId: sourcedId || '',
      mode,
      contactNumbers: sanitizedContacts.slice(0, 2),
      addressChanged,
      newAddress: enrichedAddress,
      documentName: supportingDocument ? supportingDocument.name : null,
      transportation: transportation,
      changedByParent: getChangedByParentFlag(),
    };

    setPreparedPayload(payload);
    setShowConfirmDialog(true);
    setApplyToAllChildren(false);
    setSelectedChildIds([]);
    setChildFetchError(null);
    setEligibleChildren([]);
  };

  const handleConfirmSubmit = async () => {
    if (!preparedPayload || !student) {
      setShowConfirmDialog(false);
      return;
    }

    const studentNumber = textOrNull(student.studentNumber);
    const primaryEnrollment = student.enrollment?.find((enrollment) => textOrNull(enrollment.schoolId));
    const schoolId = primaryEnrollment ? textOrNull(primaryEnrollment.schoolId) : null;
    const sourceId = textOrNull(preparedPayload.studentId) ?? textOrNull(student.id);
    const normalizedSourceId = sourceId ?? '';

    const missingFields: string[] = [];
    if (!studentNumber) missingFields.push(locale === 'ar' ? 'رقم الطالب' : 'student number');
    if (!schoolId) missingFields.push(locale === 'ar' ? 'كود المدرسة' : 'school ID');
    if (!sourceId) missingFields.push(locale === 'ar' ? 'معرّف الطالب' : 'student ID');

    if (missingFields.length > 0) {
      setShowConfirmDialog(false);
      const message =
        locale === 'ar'
          ? `يتعذر متابعة الطلب بسبب نقص البيانات التالية: ${missingFields.join('، ')}.`
          : `Cannot continue because the following data is missing: ${missingFields.join(', ')}.`;
      setErrorMessage(message);
      setPreparedPayload(null);
      return;
    }

    // ========== Resolve address based on mode ==========
    const resolvedAddress = (() => {
      // If user changed address, use new address
      if (preparedPayload.addressChanged && preparedPayload.newAddress) {
        const next = preparedPayload.newAddress;
        return {
          emirate: textOrNull(next.emirateNameEn) ?? textOrNull(next.emirateName) ?? '',
          area: textOrNull(next.areaNameEn) ?? textOrNull(next.areaName) ?? '',
          street: textOrNull(next.fullAddressEn) ?? textOrNull(next.fullAddressAr) ?? textOrNull(next.streetName) ?? '',
          houseBuilding: textOrNull(next.houseNumber) ?? '',
          region: textOrNull(next.municipalityNameEn) ?? '',
          zone: textOrNull(next.regionNameEn) ?? '',
          plot: next.plotId ?? '',
          mainPlot: textOrNull(next.mainPlotId) ?? '',
          premises: textOrNull(next.premisesPlotId) ?? textOrNull(next.communityName) ?? '',
          latitude: formatCoordinate(next.latitude) ?? '',
          longitude: formatCoordinate(next.longitude) ?? '',
        };
      }

      // EDIT MODE: fallback to previously submitted IDH address
      if (mode === 'edit' && idhResp?.data) {
        const prev = idhResp.data;
        return {
          emirate: textOrNull(prev.emirate) ?? '',
          area: textOrNull(prev.area) ?? '',
          street: textOrNull(prev.street) ?? '',
          houseBuilding: textOrNull(prev.houseBuilding) ?? '',
          region: textOrNull(prev.region) ?? '',
          zone: textOrNull(prev.zone) ?? '',
          plot: textOrNull(prev.plot) ?? '',
          mainPlot: textOrNull(prev.mainPlot) ?? '',
          premises: textOrNull(prev.premises) ?? '',
          latitude: textOrNull(prev.latitude) ?? '',
          longitude: textOrNull(prev.longitude) ?? '',
        };
      }

      // INIT MODE: fallback to OneRoster address
      if (primaryAddress) {
        return {
          emirate: textOrNull(primaryAddress.state) ?? '',
          area: textOrNull(primaryAddress.city) ?? '',
          street: textOrNull(primaryAddress.addressLine1) ?? '',
          houseBuilding: textOrNull(primaryAddress.addressLine2) ?? '',
          region: textOrNull(primaryAddress.region) ?? '',
          zone: textOrNull(primaryAddress.sector) ?? '',
          plot: textOrNull(primaryAddress.plotNumber) ?? '',
          mainPlot: textOrNull(primaryAddress.plotId) ?? '',
          premises: textOrNull(primaryAddress.addressLine3) ?? '',
          latitude: '',
          longitude: '',
        };
      }

      return {
        emirate: '',
        area: '',
        street: '',
        houseBuilding: '',
        region: '',
        zone: '',
        plot: '',
        mainPlot: '',
        premises: '',
        latitude: '',
        longitude: '',
      };
    })();

    setErrorMessage(null);
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      if (preparedPayload.addressChanged && !supportingDocument) {
        if (!isAbuDhabiAddress(preparedPayload.newAddress)) {
          throw new Error(
            locale === 'ar'
              ? 'الرجاء إعادة إرفاق المستند قبل الإرسال.'
              : 'Please reattach the supporting document before submitting.'
          );
        }
      }

      // ========== Handle attachment based on mode and user action ==========
      let fileGuid = '';

      if (supportingDocument) {
        // User uploaded a new document - upload via useUploadAttachment hook
        const uploadResult = await uploadAttachment({
          file: supportingDocument,
          referenceNumber: sourceId!,
          referenceName: studentNumber!,
          locale,
        });
        fileGuid = uploadResult.fileGuid;
      } else if (mode === 'edit' && idhResp?.data?.attachment01) {
        // EDIT mode: No new document uploaded, reuse existing fileGuid from IDH
        fileGuid = idhResp.data.attachment01;
      }
      // INIT mode with no document will remain empty string

      // ========== Extract ManhalCodes from newAddress (already fetched in handleSubmit) ==========
      let stateID: string | undefined;
      let cityID: string | undefined;
      let regionID: string | undefined;
      let sectorID: string | undefined;

      if (preparedPayload.addressChanged && preparedPayload.newAddress) {
        const newAddr = preparedPayload.newAddress;

        // ManhalCodes should already be present in newAddress from handleSubmit
        stateID = textOrNull(newAddr.emirateManhalCode) ?? undefined;
        cityID = textOrNull(newAddr.regionManhalCode) ?? undefined;
        regionID = textOrNull(newAddr.zoneManhalCode) ?? undefined;
        sectorID = textOrNull(newAddr.areaManhalCode) ?? undefined;
      } else if (mode === 'edit' && idhResp?.data) {
        // EDIT MODE: Address not changed, preserve existing ManhalCodes from IDH
        stateID = textOrNull(idhResp.data.stateID) ?? undefined;
        cityID = textOrNull(idhResp.data.cityID) ?? undefined;
        regionID = textOrNull(idhResp.data.regionID) ?? undefined;
        sectorID = textOrNull(idhResp.data.sectorID) ?? undefined;
      }

      // ========== Build payload based on mode ==========
      const idhPayload: IDHStudent = {
        studentNumber: studentNumber ?? '',
        schoolId: schoolId ?? '',
        sourceId: sourceId ?? '',
        primaryPhone: preparedPayload.contactNumbers[0] ?? '',
        otherPhone: preparedPayload.contactNumbers[1] ?? '',
        transportationType: preparedPayload.transportation,
        emirate: resolvedAddress.emirate,
        area: resolvedAddress.area,
        street: resolvedAddress.street,
        houseBuilding: resolvedAddress.houseBuilding,
        region: resolvedAddress.region,
        zone: resolvedAddress.zone,
        plot: resolvedAddress.plot?.toString(),
        mainPlot: resolvedAddress.mainPlot,
        premises: resolvedAddress.premises,
        latitude: resolvedAddress.latitude,
        longitude: resolvedAddress.longitude,
        attachment01: fileGuid,
        statusId: mode === 'init' ? (!getChangedByParentFlag() ? 4 : 1) : 3, // STATUS: INIT no changes=4, INIT with changes=1, EDIT=3
        datetime: new Date().toISOString(),
        changedByParent: getChangedByParentFlag(),
        stateID: stateID,
        cityID: cityID,
        regionID: regionID,
        sectorID: sectorID,
      };

      await submitToIDH(idhPayload);

      const selectedBulkChildren =
        applyToAllChildren && selectedChildIds.length > 0
          ? eligibleChildren.filter((entry) => {
              const childId = textOrNull(entry.profile.id) ?? entry.profile.id;
              return childId && selectedChildIds.includes(childId) && childId !== normalizedSourceId;
            })
          : [];

      // IDH submission successful - now update information status via .NET API
      try {
        await updateStudentStatus(normalizedSourceId, {
          status: mode === 'init' ? (!getChangedByParentFlag() ? 4 : 1) : 3, // INIT no changes=4, INIT with changes=1, EDIT=3
          isInformationUpdated: true, // Only sent after successful IDH submission
        });

        toast.success(
          locale === 'ar' ? 'تم بنجاح' : 'Success',
          locale === 'ar'
            ? 'تم تقديم الطلب بنجاح، وجاري العمل عليه.'
            : 'Your request has been submitted successfully and is currently under review.'
        );
      } catch (statusError) {
        console.warn('Error calling information-status API:', statusError);
        toast.warning(
          locale === 'ar' ? 'تحديث جزئي' : 'Partial Update',
          locale === 'ar'
            ? 'تم حفظ البيانات محلياً، لكن حدث خطأ في الاتصال بالنظام المركزي'
            : 'Data saved locally, but error connecting to central system'
        );
        // Non-blocking: continue even if status update fails
      }

      if (selectedBulkChildren.length > 0) {
        const bulkSuccessNames: string[] = [];
        const bulkFailedNames: string[] = [];

        for (const entry of selectedBulkChildren) {
          const childProfile = entry.profile;
          const childName = getDisplayName(childProfile, locale);
          const childId = textOrNull(childProfile.id) ?? childProfile.id;
          const childStudentNumber = textOrNull(childProfile.studentNumber);
          const childEnrollment = childProfile.enrollment?.find((enrollment: any) => textOrNull(enrollment.schoolId));
          const childSchoolId = childEnrollment ? textOrNull(childEnrollment.schoolId) : null;

          if (!childStudentNumber || !childSchoolId || !childId) {
            bulkFailedNames.push(childName);
            continue;
          }

          const childPayload: IDHStudent = {
            ...idhPayload,
            studentNumber: childStudentNumber,
            schoolId: childSchoolId,
            sourceId: childId,
            changedByParent: getChangedByParentFlag(),
          };

          try {
            await submitToIDH(childPayload);

            try {
              await updateStudentStatus(childId, {
                status: idhPayload.statusId,
                isInformationUpdated: true,
              });
            } catch (childStatusError) {
              console.warn('Error calling information-status API for child', childId, childStatusError);
            }

            bulkSuccessNames.push(childName);
          } catch (childError) {
            console.error('Bulk IDH submission failed for child', childId, childError);
            bulkFailedNames.push(childName);
          }
        }

        if (bulkSuccessNames.length > 0) {
          toast.success(
            locale === 'ar' ? 'تم التحديث للأطفال الآخرين' : 'Other children updated',
            locale === 'ar'
              ? `تم تحديث ${bulkSuccessNames.length} من الأطفال المحددين: ${bulkSuccessNames.join('، ')}.`
              : `Updated ${bulkSuccessNames.length} selected child(ren): ${bulkSuccessNames.join(', ')}.`
          );
        }

        if (bulkFailedNames.length > 0) {
          toast.warning(
            locale === 'ar' ? 'تعذر تحديث بعض الأطفال' : 'Some children were not updated',
            locale === 'ar'
              ? `يرجى المحاولة لاحقًا للأطفال: ${bulkFailedNames.join('، ')}.`
              : `Please try again for: ${bulkFailedNames.join(', ')}.`
          );
        }
      }

      setShowSuccessToast(true);
      setHasUnsavedChanges(false);

      // Clear SWR cache for student data and IDH data to ensure fresh data on next load
      await mutate((key) => typeof key === 'string' && key.includes('/students'), undefined, { revalidate: true });

      await new Promise((resolve) => setTimeout(resolve, 1500));

      // After successful update, navigate to my applications page
      navigate('/myapplications');
    } catch (submitError: unknown) {
      console.error('IDH submission failed', submitError);
      const status =
        typeof submitError === 'object' && submitError && 'status' in submitError
          ? Number((submitError as { status?: number }).status)
          : undefined;
      const fallbackMessage =
        locale === 'ar'
          ? 'حدث خطأ أثناء حفظ البيانات. يرجى المحاولة مرة أخرى.'
          : 'Something went wrong while saving. Please try again.';

      if (status === 413) {
        const upstreamMessage = (() => {
          if (
            !(typeof submitError === 'object' && submitError && 'message' in submitError) ||
            typeof (submitError as any).message !== 'string' ||
            (submitError as any).message === 'IDH submission failed'
          ) {
            return null;
          }
          const raw = String((submitError as any).message).trim();
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed.error === 'string') {
              return parsed.error;
            }
          } catch {
            // not JSON, fall back to raw string
          }
          return raw;
        })();
        const rawSize = supportingDocument ? formatBytes(supportingDocument.size) : '';
        const encodedSize = supportingDocument ? formatBytes(Math.ceil((supportingDocument.size ?? 0) / 3) * 4) : '';
        const composed =
          locale === 'ar'
            ? `${upstreamMessage ?? 'الملف المرفق كبير جداً.'} حجم الملف الحالي ${rawSize || 'غير معروف'} (حوالي ${encodedSize || '—'} بعد الترميز). يرجى تقليل الحجم إلى أقل من ${ATTACHMENT_LIMIT_LABEL}.`
            : `${upstreamMessage ?? 'The attachment is too large.'} Your file size is ${rawSize || 'unknown'} (≈ ${encodedSize || '—'} once encoded). Please reduce it below ${ATTACHMENT_LIMIT_LABEL}.`;
        setErrorMessage(composed.trim());
        toast.error(locale === 'ar' ? 'خطأ' : 'Error', composed.trim());
      } else {
        const msg =
          typeof submitError === 'object' && submitError && 'message' in submitError
            ? String((submitError as any).message)
            : fallbackMessage;
        setErrorMessage(msg || fallbackMessage);
        toast.error(locale === 'ar' ? 'خطأ' : 'Error', msg || fallbackMessage);
      }
      const errorEl = document.getElementById('form-error-message');
      if (errorEl) {
        errorEl.setAttribute('role', 'alert');
        errorEl.setAttribute('aria-live', 'assertive');
      }
    } finally {
      setIsSubmitting(false);
      setPreparedPayload(null);
    }
  };

  if (!sourcedId) {
    return (
      <div className="max-w-xl mx-auto py-10 text-center text-destructive">
        {t?.parentConduct?.noStudentId || 'No student ID provided'}
      </div>
    );
  }

  if (!updateInfo) {
    return (
      <div className="max-w-xl mx-auto py-10 text-center text-destructive">
        {locale === 'ar' ? 'خطأ في تحميل الترجمات' : 'Translation loading error. Please refresh the page.'}
      </div>
    );
  }

  if (isLoading) {
    return <Spinner />;
  }

  if (studentError || !student) {
    return <StatusCard message={studentError?.title} description={t.child.no_data_available_for_child} />;
  }

  // Stop if there is a pending request
  const statusId = idhResp?.data?.statusId;
  if (statusId != null && ([1, 3].includes(statusId) || statusId > 4)) {
    return (
      <StatusCard
        variant="error"
        message={locale === 'ar' ? 'لا يمكن إرسال طلب آخر' : 'Cannot submit another request'}
        description={
          locale === 'ar'
            ? 'يوجد بالفعل طلب تحديث بيانات قيد المعالجة لهذا الطالب.'
            : 'An update request for this student has already been submitted and is currently being processed.'
        }
      />
    );
  }

  // Stop if an application is already approved
  if (idhResp?.data?.statusId === 4) {
    return (
      <StatusCard
        variant="success"
        message={locale === 'ar' ? 'تمت الموافقة على طلب التحديث' : 'Update request already approved'}
        description={
          locale === 'ar'
            ? 'تم اعتماد طلب تحديث البيانات لهذا الطالب بنجاح.'
            : 'The update request for this student has already been approved.'
        }
      />
    );
  }

  // Redirect returned applications to resubmission
  if (mode === 'init' && idhResp?.data?.statusId === 2) {
    navigate(`/students/${sourcedId}/update-info?mode=resubmit`);
    return (
      <StatusCard
        message={locale === 'ar' ? 'إعادة التوجيه إلى صفحة إعادة الإرسال...' : 'Redirecting to resubmission...'}
      />
    );
  }

  // Redirect returned applications to resubmission
  if (mode === 'edit' && idhError?.status === 404) {
    navigate(`/students/${sourcedId}/update-info?mode=init`);
    return (
      <StatusCard
        message={locale === 'ar' ? 'إعادة التوجيه إلى صفحة إنشاء طلب جديد...' : 'Redirecting to create new request...'}
      />
    );
  }

  const displayName = getDisplayName(student, locale);

  return (
    <div className={clsx(locale === 'ar' && 'direction-rtl')}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-20 -left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '4s' }}
        />
        <div
          className="absolute top-40 -right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '6s', animationDelay: '1s' }}
        />
        <div
          className="absolute bottom-20 left-1/3 w-80 h-80 bg-primary/4 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '5s', animationDelay: '2s' }}
        />
      </div>

      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-form"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {locale === 'ar' ? 'الانتقال إلى المحتوى الرئيسي' : 'Skip to main content'}
      </a>

      <header role="banner">
        <div className="">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: locale === 'ar' ? 'الرئيسية' : 'Home', href: '/dashboard' },
              { label: locale === 'ar' ? 'عرض الملف' : 'View Profile', href: `/students/${sourcedId}` },
              { label: locale === 'ar' ? 'تحديث معلومات الطالب' : 'Update Student Information' },
            ]}
          />
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 py-4">
            <div className="flex-1">
              <h1
                className={clsx(
                  'text-xl sm:text-2xl lg:text-3xl font-bold text-foreground',
                  locale === 'ar' && 'text-right'
                )}
              >
                {updateInfo.title}
              </h1>
              <p
                className={clsx(
                  'text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl',
                  locale === 'ar' && 'text-right'
                )}
              >
                {mode === 'edit' ? updateInfo.intro.edit : updateInfo.intro.init}
              </p>
            </div>
            <Badge
              variant={mode === 'edit' ? 'destructive' : 'secondary'}
              className="self-start text-xs sm:text-sm px-3 py-1.5"
            >
              {mode === 'edit' ? updateInfo.modeLabel.edit : updateInfo.modeLabel.init}
            </Badge>
          </div>
        </div>
      </header>

      <main id="main-form" className="space-y-6" role="main">
        {/* Student Info Card */}
        <Card
          className="border border-primary/20 shadow-sm hover:shadow-lg hover:border-primary/40 transition-all duration-500 animate-in fade-in slide-in-from-bottom-4 hover:scale-[1.01]"
          style={{ animationDelay: '100ms' }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-base sm:text-lg flex items-center gap-3">
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary font-semibold text-lg sm:text-xl ring-2 ring-primary/20 ring-offset-2 ring-offset-background transition-all duration-300 hover:scale-110 hover:ring-primary/40 hover:shadow-lg"
                aria-hidden="true"
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <span className="block">{displayName || sourcedId}</span>
                {meta?.cache?.source && (
                  <span className="text-xs text-muted-foreground font-normal mt-1 flex items-center gap-1.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {locale === 'ar' ? 'آخر تحديث' : 'Last updated'}
                    {meta.cache.lastUpdated && (
                      <span className="text-[10px]">
                        ({new Date(meta.cache.lastUpdated).toLocaleString(locale === 'ar' ? 'ar-AE' : 'en-US')})
                      </span>
                    )}
                  </span>
                )}
              </div>
            </CardTitle>
          </CardHeader>
        </Card>

        {/* Success Toast */}
        {showSuccessToast && (
          <div
            className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400 flex items-center gap-3 animate-in slide-in-from-top-2"
            role="status"
            aria-live="polite"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              {locale === 'ar' ? 'تم إرسال طلب تحديث البيانات بنجاح!' : 'Your data update request has been submitted!'}
            </span>
          </div>
        )}

        {/* IDH Return Comment (edit mode) - Sticky */}
        {mode === 'edit' && idhResp?.data?.ReturnComment && textOrNull(idhResp.data.ReturnComment) && (
          <div
            className="sticky top-0 z-30 rounded-lg border-2 border-amber-500/60 bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/40 dark:via-amber-900/60 dark:to-amber-900/40 px-5 py-4 text-sm shadow-lg backdrop-blur-sm animate-in slide-in-from-top-4"
            role="alert"
            aria-live="assertive"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-amber-500/20 shrink-0">
                <svg
                  className="w-6 h-6 text-amber-700 dark:text-amber-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="flex-1 space-y-2">
                <p className="font-bold text-base text-amber-900 dark:text-amber-100">
                  {locale === 'ar' ? '⚠️ سبب الإرجاع' : '⚠️ Return Reason'}
                </p>
                <p className="whitespace-pre-wrap break-words text-amber-800 dark:text-amber-200 leading-relaxed font-medium">
                  {idhResp.data.ReturnComment}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 pt-1 border-t border-amber-300/30">
                  {locale === 'ar'
                    ? 'الرجاء مراجعة هذا السبب وتحديث المعلومات المطلوبة قبل إعادة الإرسال'
                    : 'Please review this reason and update the required information before resubmitting'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div
            id="form-error-message"
            ref={firstErrorRef}
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center gap-3 animate-in slide-in-from-top-2"
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{errorMessage}</span>
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" aria-label={updateInfo.title} noValidate>
          {/* Progress Indicator - Hidden */}
          <div className="hidden" role="status" aria-live="polite">
            <span className="text-xs sm:text-sm text-muted-foreground">
              {locale === 'ar' ? 'التقدم:' : 'Progress:'}
            </span>
            <div className="flex items-center gap-2">
              <div className="h-2 w-24 sm:w-32 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{
                    width: `${(contactNumbers.filter((n) => n.trim()).length > 0 ? 33 : 0) + (addressChanged && newAddress ? 33 : 0) + (transportation ? 34 : 0)}%`,
                  }}
                  role="progressbar"
                  aria-valuenow={
                    (contactNumbers.filter((n) => n.trim()).length > 0 ? 33 : 0) +
                    (addressChanged && newAddress ? 33 : 0) +
                    (transportation ? 34 : 0)
                  }
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={locale === 'ar' ? 'نسبة الإكمال' : 'Completion percentage'}
                />
              </div>
              <span className="text-xs font-medium text-foreground tabular-nums">
                {Math.round(
                  (contactNumbers.filter((n) => n.trim()).length > 0 ? 33 : 0) +
                    (addressChanged && newAddress ? 33 : 0) +
                    (transportation ? 34 : 0)
                )}
                %
              </span>
            </div>
          </div>

          <Card className="shadow-md border-2 border-border/40 bg-card/50 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent border-b border-border/40">
              <CardTitle className="text-lg sm:text-xl text-foreground flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <span>{updateInfo.contactSection.title}</span>
              </CardTitle>
              <CardDescription
                className={clsx('text-sm text-muted-foreground mt-1.5', locale === 'ar' && 'text-right')}
              >
                {updateInfo.contactSection.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-5 mt-5">
              <fieldset className="space-y-4">
                <legend className="sr-only">{updateInfo.contactSection.title}</legend>
                {contactNumbers.map((number, index) => (
                  <div key={index} className="space-y-2">
                    <Label
                      htmlFor={`contact-${index}`}
                      className={clsx(
                        'text-sm font-medium flex items-center gap-2',
                        locale === 'ar' && 'flex-row-reverse justify-end'
                      )}
                    >
                      <span>
                        {index === 0
                          ? updateInfo.contactSection.primaryLabel
                          : updateInfo.contactSection.secondaryLabel}
                      </span>
                      {index === 0 && (
                        <>
                          <Badge variant="default" className="text-xs px-2 py-0.5">
                            {locale === 'ar' ? 'أساسي' : 'Primary'}
                          </Badge>
                          <span className="text-destructive" aria-label={locale === 'ar' ? 'مطلوب' : 'required'}>
                            *
                          </span>
                        </>
                      )}
                    </Label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          id={`contact-${index}`}
                          type="tel"
                          inputMode="tel"
                          value={number}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleContactChange(index, event.target.value)
                          }
                          placeholder="05XXXXXXXX"
                          dir="ltr"
                          className={clsx(
                            'w-full h-11 bg-background border-2',
                            getContactNumberError(number, index)
                              ? 'border-destructive focus:border-destructive focus-visible:ring-destructive/20'
                              : 'border-input hover:border-primary/50 focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/20',
                            'transition-all duration-200',
                            'text-foreground placeholder:text-muted-foreground/60',
                            locale === 'ar' && 'text-right',
                            isSubmitting && 'opacity-50 cursor-not-allowed'
                          )}
                          required={index === 0}
                          aria-required={index === 0}
                          aria-invalid={!!getContactNumberError(number, index)}
                          aria-describedby={`contact-${index}-help ${getContactNumberError(number, index) ? `contact-${index}-error` : ''}`}
                          disabled={
                            isSubmitting || (PRIMARY_PHONE_FROM_UAEPASS && !ALLOW_CHANGING_PRIMARY_PHONE && index === 0)
                          }
                        />
                      </div>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleContactChange(index, '')}
                          disabled={isSubmitting}
                          aria-label={`${locale === 'ar' ? 'مسح' : 'Clear'} ${index + 1}`}
                          className={clsx(
                            'shrink-0 h-11 border-2',
                            'hover:bg-muted hover:text-foreground hover:border-border',
                            'transition-all duration-200'
                          )}
                        >
                          <span className="hidden sm:inline">{locale === 'ar' ? 'مسح' : 'Clear'}</span>
                          <svg
                            className="w-4 h-4 sm:hidden"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </Button>
                      )}
                    </div>
                    {getContactNumberError(number, index) && (
                      <p
                        id={`contact-${index}-error`}
                        className="text-xs text-destructive flex items-center gap-1.5 animate-in slide-in-from-top-1"
                      >
                        <svg
                          className="w-3.5 h-3.5 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>{getContactNumberError(number, index)}</span>
                      </p>
                    )}
                    {!getContactNumberError(number, index) && (
                      <p
                        id={`contact-${index}-help`}
                        className="text-xs text-muted-foreground flex items-center gap-1.5"
                      >
                        <svg
                          className="w-3.5 h-3.5 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>
                          {index === 0
                            ? locale === 'ar'
                              ? 'رقم الهاتف المحمول الرئيسي للتواصل العاجل (05XXXXXXXX)'
                              : 'Primary mobile number for urgent contact (05XXXXXXXX)'
                            : locale === 'ar'
                              ? 'رقم الهاتف المحمول الإضافي (اختياري) (05XXXXXXXX)'
                              : 'Additional mobile number (optional) (05XXXXXXXX)'}
                        </span>
                      </p>
                    )}
                  </div>
                ))}
              </fieldset>

              {contactNumbers.length < 2 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddContact}
                  disabled={isSubmitting}
                  className={clsx(
                    'w-full sm:w-auto h-11 border-2 border-dashed',
                    'bg-secondary/50 hover:bg-secondary hover:border-primary/50',
                    'transition-all duration-200'
                  )}
                  aria-label={updateInfo.contactSection.addButton}
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {updateInfo.contactSection.addButton}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card
            className="shadow-md border-2 border-border/40 bg-card/50 backdrop-blur-sm hover:shadow-xl hover:border-primary/30 transition-all duration-500 animate-in fade-in slide-in-from-bottom-4 group"
            style={{ animationDelay: '300ms' }}
          >
            <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent border-b border-border/40 group-hover:from-primary/10 transition-all duration-500">
              <CardTitle className="text-lg sm:text-xl text-foreground flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <span>{updateInfo.addressSection.title}</span>
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground  !mt-5">
                {updateInfo.addressSection.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-5">
              {/* Transportation Section */}
              <div className="space-y-2 pb-4 border-b border-border/30 mt-5">
                <Label
                  htmlFor="transportation-method"
                  className={clsx(
                    'text-sm font-medium flex items-center gap-2',
                    locale === 'ar' && 'flex-row-reverse justify-end'
                  )}
                >
                  <svg
                    className="w-5 h-5 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  <span>{updateInfo.transportationSection.selectLabel}</span>
                </Label>
                <p className="text-xs text-muted-foreground">{updateInfo.transportationSection.description}</p>
                <Select
                  key={`transportation-${mode}-${transportation}`}
                  value={transportation}
                  onValueChange={(value: string) => setTransportation(value)}
                  disabled={isSubmitting || !ALLOW_CHANGING_TRANSPORT_MODE}
                >
                  <SelectTrigger
                    id="transportation-method"
                    aria-invalid={errorMessage?.includes('transportation')}
                    className={clsx(
                      'h-11 border-2 bg-background hover:border-primary/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200',
                      !ALLOW_CHANGING_TRANSPORT_MODE && 'cursor-not-allowed opacity-75',
                      locale === 'ar' && 'text-right'
                    )}
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  >
                    <SelectValue placeholder={locale === 'ar' ? 'اختر طريقة المواصلات' : 'Select a method'} />
                  </SelectTrigger>
                  <SelectContent
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                    className={clsx(locale === 'ar' && 'text-right')}
                  >
                    <SelectItem value="Bus">{locale === 'ar' ? 'بواسطة الحافلة' : 'By Bus'}</SelectItem>
                    <SelectItem value="Car">{locale === 'ar' ? 'بالسيارة' : 'By Car'}</SelectItem>
                    <SelectItem value="On Foot">{locale === 'ar' ? 'سيراً على الأقدام' : 'On Foot'}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Alert message for disabled transportation change */}
                {!ALLOW_CHANGING_TRANSPORT_MODE && (
                  <div
                    className="rounded-xl border-2 border-amber-500/40 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 p-2 hover:border-amber-500/60 transition-all duration-500 flex gap-x-3"
                    role="alert"
                    aria-live="polite"
                  >
                    <svg
                      className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm sm:text-base text-amber-900/90 dark:text-amber-100/90">
                        {locale === 'ar'
                          ? 'يرجى العلم بأنه لا يمكن تغيير وسيلة النقل حالياً. ولإجراء أي تعديل على وسيلة النقل، يرجى التواصل مباشرةً مع المدرسة.'
                          : 'Please note that the transportation cannot be changed at this time. To make any changes to the transportation arrangement, please contact the school directly.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="my-5">
                <Label
                  className={clsx(
                    'text-sm font-medium block mb-2 flex items-center gap-2',
                    locale === 'ar' && 'flex-row-reverse justify-end'
                  )}
                >
                  <svg
                    className="w-4 h-4 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{updateInfo.addressSection.currentLabel}</span>
                  {primaryAddress?.isPrimary && (
                    <Badge variant="default" className="text-xs px-2 py-0.5">
                      {locale === 'ar' ? 'أساسي' : 'Primary'}
                    </Badge>
                  )}
                </Label>
                <div
                  className={clsx(
                    'rounded-xl border-2 border-dashed border-border/50 bg-muted/30 px-4 py-4 text-sm text-foreground/80 shadow-sm',
                    locale === 'ar' && 'text-right'
                  )}
                >
                  <p className="leading-relaxed">{formattedCurrentAddress || t.child.no_address_available}</p>
                </div>
              </div>

              {mode === 'edit' && formattedPrevSubmittedAddress && (
                <div className="my-5">
                  <Label
                    className={clsx(
                      'text-sm font-medium block mb-2 flex items-center gap-2',
                      locale === 'ar' && 'flex-row-reverse justify-end'
                    )}
                  >
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span>{locale === 'ar' ? 'العنوان المرسل سابقاً' : 'Previously submitted address'}</span>
                  </Label>
                  <div
                    className={clsx(
                      'rounded-xl border-2 border-dashed border-border/50 bg-muted/30 px-4 py-4 text-sm text-foreground/80 shadow-sm',
                      locale === 'ar' && 'text-right'
                    )}
                  >
                    <p className="leading-relaxed">{formattedPrevSubmittedAddress}</p>
                  </div>
                </div>
              )}

              {mode === 'edit' && idhAttachmentUrl && (
                <div className="my-5">
                  <Label
                    className={clsx(
                      'text-sm font-medium block mb-2 flex items-center gap-2',
                      locale === 'ar' && 'flex-row-reverse justify-end'
                    )}
                  >
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12H9m0 0l3 3m-3-3l3-3m7 9V6a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2z"
                      />
                    </svg>
                    <span>{locale === 'ar' ? 'المستند المرفوع سابقًا' : 'Previously uploaded document'}</span>
                  </Label>
                  <div
                    className={clsx(
                      'rounded-xl border-2 border-dashed border-border/50 bg-muted/30 px-4 py-4 text-sm text-foreground/80 shadow-sm flex items-center justify-between gap-3',
                      locale === 'ar' && 'flex-row-reverse text-right'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-5 h-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v8m4-4H8m11 8H5a2 2 0 01-2-2V6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="font-medium">{locale === 'ar' ? 'ملف PDF' : 'PDF file'}</span>
                      {idhAttachmentSize > 0 && (
                        <span className="text-xs text-muted-foreground">({formatBytes(idhAttachmentSize)})</span>
                      )}
                    </div>
                    <a
                      href={idhAttachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download="supporting-document.pdf"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                        />
                      </svg>
                      <span>{locale === 'ar' ? 'عرض/تحميل' : 'View / Download'}</span>
                    </a>
                  </div>
                </div>
              )}

              <div className="bg-primary/5 border-2 border-primary/20 rounded-xl p-4">
                <label
                  className={clsx(
                    'flex items-start gap-3 text-sm font-medium text-foreground cursor-pointer group',
                    locale === 'ar' && 'flex-row text-right'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={addressChanged}
                    onChange={(event) => setAddressChanged(event.target.checked)}
                    className="h-5 w-5 mt-0.5 rounded border-2 border-primary/40 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all cursor-pointer"
                    disabled={isSubmitting}
                    aria-describedby="address-change-help"
                  />
                  <div className="flex-1">
                    <span className="group-hover:text-primary transition-colors block">
                      {updateInfo.addressSection.changeToggle}
                    </span>
                    <p id="address-change-help" className="text-xs text-muted-foreground mt-1.5">
                      {locale === 'ar'
                        ? 'حدد هذا الخيار إذا انتقلت إلى عنوان جديد'
                        : 'Check this option if you have moved to a new address'}
                    </p>
                  </div>
                </label>
              </div>

              {addressChanged && (
                <div className="animate-in fade-in-50 duration-300">
                  <div className="space-y-3">
                    <Label
                      className={clsx(
                        'text-sm font-medium text-foreground flex items-center gap-2',
                        locale === 'ar' && 'flex-row-reverse justify-end'
                      )}
                    >
                      <span>{updateInfo.addressSection.newAddressLabel}</span>
                      <span className="text-destructive" aria-label={locale === 'ar' ? 'مطلوب' : 'required'}>
                        *
                      </span>
                    </Label>
                    <AddressPicker
                      value={newAddress ?? undefined}
                      onChange={(value: any) => setNewAddress(value)}
                      disabled={!addressChanged || isSubmitting}
                      required={{ emirate: true, area: true }}
                    />
                  </div>
                </div>
              )}

              {addressChanged &&
                (() => {
                  const isAbuDhabi = isAbuDhabiAddress(newAddress);

                  // In EDIT mode with existing attachment, show it; in INIT mode or non-Abu Dhabi, show file upload
                  const hasExistingAttachment = mode === 'edit' && !!idhAttachmentUrl;
                  const shouldShowFileUpload = !isAbuDhabi || hasExistingAttachment;

                  return shouldShowFileUpload ? (
                    <div className="space-y-4 animate-in fade-in-50 duration-300">
                      <Label
                        htmlFor="address-document"
                        className={clsx(
                          'text-sm font-medium flex items-center gap-2',
                          locale === 'ar' && 'flex-row-reverse justify-end'
                        )}
                      >
                        <svg
                          className="w-5 h-5 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                          />
                        </svg>
                        <span>{updateInfo.addressSection.documentLabel}</span>
                        {!isAbuDhabi && (
                          <span className="text-destructive" aria-label={locale === 'ar' ? 'مطلوب' : 'required'}>
                            *
                          </span>
                        )}
                      </Label>

                      {/* File Requirements Info Card */}
                      <div className="rounded-xl bg-gradient-to-br from-primary/5 via-primary/3 to-transparent border-2 border-primary/20 p-4 space-y-3 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                            <svg
                              className="w-5 h-5 text-primary"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1 space-y-2">
                            <p className="text-sm font-semibold text-foreground">
                              {locale === 'ar' ? 'متطلبات الملف' : 'File Requirements'}
                            </p>
                            <ul className="space-y-1.5 text-xs text-muted-foreground">
                              <li className="flex items-center gap-2">
                                <svg
                                  className="w-4 h-4 text-primary shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span className="font-medium">
                                  {locale === 'ar' ? 'الصيغة: PDF فقط' : 'Format: PDF only'}
                                </span>
                              </li>
                              <li className="flex items-center gap-2">
                                <svg
                                  className="w-4 h-4 text-primary shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span className="font-medium">
                                  {locale === 'ar' ? 'الحد الأقصى للحجم: 5 ميجابايت' : 'Maximum size: 5 MB'}
                                </span>
                              </li>
                              <li className="flex items-center gap-2">
                                <svg
                                  className="w-4 h-4 text-primary shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span>
                                  {locale === 'ar'
                                    ? 'المستندات المقبولة: فاتورة خدمات، عقد الإيجار، أو مستند رسمي'
                                    : 'Accepted: Services bill, tenancy contract, or official document'}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* File Upload Area */}
                      <div
                        className={clsx(
                          'relative rounded-xl border-2 border-dashed transition-all duration-300',
                          addressChanged && !supportingDocument
                            ? 'border-primary/40 bg-primary/5 hover:border-primary hover:bg-primary/10'
                            : supportingDocument
                              ? 'border-green-500/40 bg-green-500/5'
                              : 'border-border/30 bg-muted/20',
                          (!addressChanged || isSubmitting) && 'opacity-60 cursor-not-allowed'
                        )}
                      >
                        <Input
                          id="address-document"
                          type="file"
                          accept=".pdf,application/pdf"
                          disabled={!addressChanged || isSubmitting}
                          onChange={handleFileChange}
                          className={clsx(
                            'absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10',
                            (!addressChanged || isSubmitting) && 'cursor-not-allowed'
                          )}
                          required={addressChanged && !isAbuDhabi}
                          aria-required={addressChanged && !isAbuDhabi}
                          aria-describedby="address-document-help address-document-status"
                          aria-invalid={addressChanged && errorMessage?.includes('document')}
                        />

                        <div className="p-6 text-center pointer-events-none">
                          {!supportingDocument ? (
                            <div className="space-y-3">
                              <div className="flex justify-center">
                                <div className="p-4 rounded-full bg-primary/10">
                                  <svg
                                    className="w-10 h-10 text-primary"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                    />
                                  </svg>
                                </div>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm font-semibold text-foreground">
                                  {locale === 'ar' ? 'انقر لاختيار ملف PDF' : 'Click to select a PDF file'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {locale === 'ar' ? 'أو اسحب الملف وأفلته هنا' : 'or drag and drop it here'}
                                </p>
                              </div>
                              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                <span className="font-medium">
                                  {locale === 'ar' ? 'PDF • حتى 5 ميجابايت' : 'PDF • Up to 5 MB'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex justify-center">
                                <div className="p-4 rounded-full bg-green-500/10">
                                  <svg
                                    className="w-10 h-10 text-green-600 dark:text-green-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                                  {locale === 'ar' ? 'تم اختيار الملف بنجاح' : 'File selected successfully'}
                                </p>
                                <div className="flex items-center justify-center gap-2 text-xs">
                                  <svg
                                    className="w-4 h-4 text-muted-foreground"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                  </svg>
                                  <span className="font-semibold text-foreground truncate max-w-xs">
                                    {supportingDocument.name}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {(supportingDocument.size / (1024 * 1024)).toFixed(2)} MB{' '}
                                  {locale === 'ar' ? 'من 5 ميجابايت' : 'of 5 MB'}
                                </p>
                                <p className="text-xs text-primary font-medium mt-2">
                                  {locale === 'ar' ? 'انقر لتغيير الملف' : 'Click to change file'}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status and Helper Text */}
                      <div className="space-y-2">
                        {!supportingDocument && addressChanged && (
                          <p
                            id="address-document-status"
                            className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5"
                          >
                            <svg
                              className="w-4 h-4 shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            {updateInfo.noFileSelected}
                          </p>
                        )}
                        <p
                          id="address-document-help"
                          className="text-xs text-muted-foreground flex items-start gap-1.5"
                        >
                          <svg
                            className="w-4 h-4 shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>{updateInfo.addressSection.documentHelper}</span>
                        </p>
                      </div>
                    </div>
                  ) : null;
                })()}
            </CardContent>
          </Card>

          {/* Important Notice Alert */}
          <div
            className="rounded-xl border-2 border-amber-500/40 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-amber-500/60 transition-all duration-500 animate-in fade-in slide-in-from-bottom-4"
            style={{ animationDelay: '500ms' }}
            role="alert"
            aria-labelledby="important-notice-title"
          >
            <div className="flex gap-4">
              <div className="shrink-0">
                <div
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-500/20 flex items-center justify-center animate-pulse"
                  style={{ animationDuration: '3s' }}
                >
                  <svg
                    className="w-6 h-6 sm:w-7 sm:h-7 text-amber-600 dark:text-amber-500 animate-bounce"
                    style={{ animationDuration: '2s' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1 space-y-3">
                <h3
                  id="important-notice-title"
                  className={clsx(
                    'text-base sm:text-lg font-bold text-amber-900 dark:text-amber-200 flex items-center gap-2',
                    locale === 'ar' && 'text-right'
                  )}
                >
                  <span>
                    {locale === 'ar' ? 'تنبيه هام - يرجى القراءة بعناية' : 'Important Notice - Please Read Carefully'}
                  </span>
                </h3>
                <div
                  className={clsx(
                    'space-y-2.5 text-sm sm:text-base text-amber-900/90 dark:text-amber-100/90',
                    locale === 'ar' && 'text-right'
                  )}
                >
                  <p className="leading-relaxed font-medium">
                    {locale === 'ar'
                      ? 'المعلومات غير الصحيحة قد تؤثر على خدمات طفلك المدرسية وقد تتطلب تحديثات لاحقة.'
                      : "Incorrect information may affect your child's school services and may require later updates."}
                  </p>
                  <ul
                    className={clsx(
                      'space-y-2 list-disc',
                      locale === 'ar' ? 'mr-4 list-inside text-right' : 'ml-4 list-inside'
                    )}
                  >
                    <li className="leading-relaxed">
                      {locale === 'ar'
                        ? 'تحقق من صحة أرقام الاتصال وإمكانية الوصول إليها'
                        : 'Verify that contact numbers are correct and reachable'}
                    </li>
                    <li className="leading-relaxed">
                      {locale === 'ar'
                        ? 'تأكد من دقة العنوان السكني وتطابقه مع المستندات الرسمية'
                        : 'Ensure the residential address matches official documents'}
                    </li>
                    <li className="leading-relaxed">
                      {locale === 'ar'
                        ? 'راجع المستند المرفق للتأكد من وضوحه وصحته'
                        : 'Review uploaded documents for clarity and validity'}
                    </li>
                    <li className="leading-relaxed">
                      {locale === 'ar'
                        ? 'تأكد من دقة معلومات النقل المدرسي'
                        : 'Confirm transportation details are accurate'}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div
            className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 border-t border-border/60 pt-6 mt-8 animate-in fade-in slide-in-from-bottom-4"
            style={{ animationDelay: '600ms' }}
          >
            <button
              type="button"
              onClick={() => {
                if (
                  hasUnsavedChanges &&
                  !window.confirm(
                    locale === 'ar'
                      ? 'لديك تغييرات غير محفوظة. هل أنت متأكد من الرجوع؟'
                      : 'You have unsaved changes. Are you sure you want to go back?'
                  )
                ) {
                  return;
                }
                navigate(`/students/${encodeURIComponent(sourcedId)}`);
              }}
              className="aegov-btn btn-soft btn-secondary btn-sm"
              disabled={isSubmitting}
              aria-label={t.back}
            >
              {locale === 'ar' ? <CaretRightIcon size={20} /> : <CaretLeftIcon size={20} />}
              {t.back}
            </button>
            <button
              type="submit"
              className="aegov-btn btn-sm group"
              disabled={isSubmitting || isFetchingManhalCodes}
              aria-label={
                isSubmitting || isFetchingManhalCodes ? updateInfo.submit.submitting : updateInfo.submit.continue
              }
            >
              <span>
                {isFetchingManhalCodes
                  ? locale === 'ar'
                    ? 'جارٍ التحميل...'
                    : 'Loading...'
                  : isSubmitting
                    ? updateInfo.submit.submitting
                    : updateInfo.submit.continue}
              </span>
              {(isSubmitting || isFetchingManhalCodes) && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {!(isSubmitting || isFetchingManhalCodes) && (
                <>{locale === 'ar' ? <CaretLeftIcon size={20} /> : <CaretRightIcon size={20} />}</>
              )}
            </button>
          </div>
        </form>
      </main>
      {/* Keyboard Navigation Hints */}
      <div className="sr-only" role="region" aria-label={locale === 'ar' ? 'تلميحات لوحة المفاتيح' : 'Keyboard hints'}>
        <p>{locale === 'ar' ? 'استخدم Tab للتنقل بين الحقول' : 'Use Tab to navigate between fields'}</p>
        <p>{locale === 'ar' ? 'اضغط Enter لإرسال النموذج' : 'Press Enter to submit the form'}</p>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 fade-in duration-300">
          <DialogHeader className="space-y-3 pb-2">
            <DialogTitle
              className={clsx('flex items-center gap-3 text-xl font-bold', locale === 'ar' && 'flex-row text-right')}
            >
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <svg
                  className="w-7 h-7 text-amber-600 dark:text-amber-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span>{locale === 'ar' ? 'ملخص' : 'Summary'}</span>
            </DialogTitle>
            <DialogDescription
              className={clsx('text-base leading-relaxed', locale === 'ar' ? 'text-right' : 'text-left')}
            >
              {locale === 'ar'
                ? 'يرجى مراجعة معلومات الطالب قبل المتابعة.'
                : 'Please review the student information before proceeding.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pb-6">
            {/* Contact Numbers Summary */}
            <div className="rounded-lg border-2 border-border/50 bg-card/30 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary/5 to-transparent border-b border-border/40">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <svg
                    className="w-5 h-5 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-base text-foreground">
                  {locale === 'ar' ? 'أرقام الاتصال' : 'Contact Numbers'}
                </h4>
              </div>
              <div className="p-4 space-y-3">
                <div className={clsx('flex items-center gap-3', locale === 'ar' && 'flex-row justify-start')}>
                  <Badge variant="secondary" className="shrink-0">
                    {locale === 'ar' ? 'الأساسي' : 'Primary'}
                  </Badge>
                  <span className="font-mono text-base font-medium text-foreground" dir="ltr">
                    {preparedPayload?.contactNumbers[0] || '—'}
                  </span>
                </div>
                {preparedPayload?.contactNumbers[1] && (
                  <div className={clsx('flex items-center gap-3', locale === 'ar' && 'flex-row justify-start')}>
                    <Badge variant="outline" className="shrink-0">
                      {locale === 'ar' ? 'الثانوي' : 'Secondary'}
                    </Badge>
                    <span className="font-mono text-base font-medium text-foreground" dir="ltr">
                      {preparedPayload.contactNumbers[1]}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Address Summary */}
            <div className="rounded-lg border-2 border-border/50 bg-card/30 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary/5 to-transparent border-b border-border/40">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <svg
                    className="w-5 h-5 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-base text-foreground">
                  {locale === 'ar' ? 'العنوان السكني' : 'Residential Address'}
                </h4>
              </div>
              <div className="p-4">
                {preparedPayload?.addressChanged ? (
                  <div className="space-y-3">
                    <Badge variant="default" className="mb-2">
                      {locale === 'ar' ? 'عنوان جديد' : 'New Address'}
                    </Badge>
                    <div className="space-y-2.5 bg-background/50 p-4 rounded-lg border border-border/40">
                      {confirmAddress.data.emirate && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'الإمارة:' : 'Emirate:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.emirate}</span>
                        </div>
                      )}
                      {confirmAddress.data.city && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'المدينة:' : 'City:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.city}</span>
                        </div>
                      )}
                      {confirmAddress.data.region && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar'
                              ? isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'المنطقة:'
                                : 'منطقة/قطاع:'
                              : isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'Region:'
                                : 'District/Sector:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.region}</span>
                        </div>
                      )}
                      {confirmAddress.data.area && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar'
                              ? isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'الحي:'
                                : 'حوض/حي:'
                              : isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'Area:'
                                : 'Sector/Area:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.area}</span>
                        </div>
                      )}
                      {confirmAddress.data.longitude && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'خط الطول:' : 'Longitude:'}
                          </span>
                          <span className="text-foreground font-medium font-mono text-sm">
                            {confirmAddress.data.longitude}
                          </span>
                        </div>
                      )}
                      {confirmAddress.data.latitude && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'خط العرض:' : 'Latitude:'}
                          </span>
                          <span className="text-foreground font-medium font-mono text-sm">
                            {confirmAddress.data.latitude}
                          </span>
                        </div>
                      )}
                      {confirmAddress.data.premises && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'الموقع/المبنى:' : 'Premises:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.premises}</span>
                        </div>
                      )}
                      {confirmAddress.data.mainPlot && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar'
                              ? isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'القطعة الرئيسية:'
                                : 'رقم الشارع:'
                              : isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'Main Plot:'
                                : 'Street Number:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.mainPlot}</span>
                        </div>
                      )}
                      {confirmAddress.data.plot && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'القطعة:' : 'Plot:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.plot}</span>
                        </div>
                      )}
                      {confirmAddress.data.street && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar'
                              ? isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'الشارع:'
                                : 'اسم الشارع:'
                              : isDubaiOrAbuDhabi(confirmAddress.data.emirate)
                                ? 'Street:'
                                : 'Street Name:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.street}</span>
                        </div>
                      )}
                      {confirmAddress.data.houseBuilding && (
                        <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'المبنى/المنزل:' : 'House / Building:'}
                          </span>
                          <span className="text-foreground font-medium">{confirmAddress.data.houseBuilding}</span>
                        </div>
                      )}
                      {preparedPayload.documentName && (
                        <div
                          className={clsx(
                            'flex gap-3 pt-3 mt-3 border-t border-border/30',
                            locale === 'ar' && 'flex-row text-right'
                          )}
                        >
                          <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                            {locale === 'ar' ? 'المستند:' : 'Document:'}
                          </span>
                          <span
                            className="text-foreground text-sm font-mono truncate flex-1"
                            title={preparedPayload.documentName}
                          >
                            📄 {preparedPayload.documentName}
                          </span>
                        </div>
                      )}

                      {/* ManhalCodes Section for New Address */}
                      {/* {(() => {
                        const manhalCodes = [];
                        if (preparedPayload.newAddress) {
                          const addr = preparedPayload.newAddress;
                          if (addr.emirateManhalCode) manhalCodes.push({ label: locale === 'ar' ? 'رمز الإمارة' : 'Emirate Code', value: addr.emirateManhalCode });
                          if (addr.regionManhalCode) manhalCodes.push({ label: locale === 'ar' ? 'رمز المدينة' : 'City Code', value: addr.regionManhalCode });
                          if (addr.zoneManhalCode) manhalCodes.push({ label: locale === 'ar' ? 'رمز المنطقة' : 'Region Code', value: addr.zoneManhalCode });
                          if (addr.areaManhalCode) manhalCodes.push({ label: locale === 'ar' ? 'رمز القطاع' : 'Sector Code', value: addr.areaManhalCode });
                        }
                        
                        if (manhalCodes.length === 0) return null;
                        
                        return (
                          <div className="pt-3 mt-3 border-t border-border/30">
                            <h5 className={clsx("text-xs font-semibold text-muted-foreground mb-2", locale === 'ar' && 'text-right')}>
                              {locale === 'ar' ? 'أكواد منهل (ManhalCodes)' : 'ManhalCodes'}
                            </h5>
                            <div className="grid grid-cols-2 gap-2">
                              {manhalCodes.map((item) => (
                                <div key={item.label} className={clsx("flex gap-2 items-baseline", locale === 'ar' && 'flex-row text-right')}>
                                  <span className="text-xs text-muted-foreground">{item.label}:</span>
                                  <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded font-medium">
                                    {item.value}
                                  </code>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()} */}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Badge variant={confirmAddress.source === 'idh' ? 'secondary' : 'outline'} className="mb-2">
                      {confirmAddress.source === 'idh'
                        ? locale === 'ar'
                          ? 'العنوان السابق'
                          : 'Previously submitted'
                        : confirmAddress.source === 'oneroster'
                          ? locale === 'ar'
                            ? 'العنوان الحالي'
                            : 'Current address'
                          : locale === 'ar'
                            ? 'لا يوجد عنوان'
                            : 'No address'}
                    </Badge>
                    {confirmAddress.source === 'empty' ? (
                      <div
                        className={clsx('text-sm text-muted-foreground italic py-2', locale === 'ar' && 'text-right')}
                      >
                        {locale === 'ar' ? 'لا توجد بيانات عنوان لعرضها' : 'No address data to display'}
                      </div>
                    ) : (
                      <div className="space-y-2.5 bg-background/50 p-4 rounded-lg border border-border/40">
                        {confirmAddress.data.emirate && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'الإمارة:' : 'Emirate:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.emirate}</span>
                          </div>
                        )}
                        {confirmAddress.data.region && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'المدينة:' : 'City:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.region}</span>
                          </div>
                        )}
                        {confirmAddress.data.zone && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'المنطقة:' : 'Region:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.zone}</span>
                          </div>
                        )}
                        {confirmAddress.data.area && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'الحي/القطاع:' : 'Sector:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.area}</span>
                          </div>
                        )}
                        {confirmAddress.data.longitude && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'خط الطول:' : 'Longitude:'}
                            </span>
                            <span className="text-foreground font-medium font-mono text-sm">
                              {confirmAddress.data.longitude}
                            </span>
                          </div>
                        )}
                        {confirmAddress.data.latitude && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'خط العرض:' : 'Latitude:'}
                            </span>
                            <span className="text-foreground font-medium font-mono text-sm">
                              {confirmAddress.data.latitude}
                            </span>
                          </div>
                        )}
                        {confirmAddress.data.premises && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'الموقع/المبنى:' : 'Premises:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.premises}</span>
                          </div>
                        )}
                        {confirmAddress.data.mainPlot && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'القطعة الرئيسية:' : 'Main Plot:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.mainPlot}</span>
                          </div>
                        )}
                        {confirmAddress.data.plot && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'القطعة:' : 'Plot:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.plot}</span>
                          </div>
                        )}
                        {confirmAddress.data.street && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'الشارع:' : 'Street:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.street}</span>
                          </div>
                        )}
                        {confirmAddress.data.houseBuilding && (
                          <div className={clsx('flex gap-3', locale === 'ar' && 'flex-row text-right')}>
                            <span className="font-medium text-muted-foreground min-w-[120px] shrink-0">
                              {locale === 'ar' ? 'المبنى/المنزل:' : 'House / Building:'}
                            </span>
                            <span className="text-foreground font-medium">{confirmAddress.data.houseBuilding}</span>
                          </div>
                        )}

                        {/* ManhalCodes Section for Previous Address */}
                        {/* {(() => {
                          const manhalCodes = [];
                          if (mode === 'edit' && idhResp?.data) {
                            const idh = idhResp.data;
                            if (idh.stateID) manhalCodes.push({ label: locale === 'ar' ? 'رمز الإمارة' : 'Emirate Code', value: idh.stateID });
                            if (idh.cityID) manhalCodes.push({ label: locale === 'ar' ? 'رمز المدينة' : 'City Code', value: idh.cityID });
                            if (idh.regionID) manhalCodes.push({ label: locale === 'ar' ? 'رمز المنطقة' : 'Region Code', value: idh.regionID });
                            if (idh.sectorID) manhalCodes.push({ label: locale === 'ar' ? 'رمز القطاع' : 'Sector Code', value: idh.sectorID });
                          }
                          
                          if (manhalCodes.length === 0) return null;
                          
                          return (
                            <div className="pt-3 mt-3 border-t border-border/30">
                              <h5 className={clsx("text-xs font-semibold text-muted-foreground mb-2", locale === 'ar' && 'text-right')}>
                                {locale === 'ar' ? 'أكواد منهل (ManhalCodes)' : 'ManhalCodes'}
                              </h5>
                              <div className="grid grid-cols-2 gap-2">
                                {manhalCodes.map((item) => (
                                  <div key={item.label} className={clsx("flex gap-2 items-baseline", locale === 'ar' && 'flex-row text-right')}>
                                    <span className="text-xs text-muted-foreground">{item.label}:</span>
                                    <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded font-medium">
                                      {item.value}
                                    </code>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()} */}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Transportation Summary */}
            <div className="rounded-lg border-2 border-border/50 bg-card/30 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary/5 to-transparent border-b border-border/40">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <svg
                    className="w-5 h-5 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-base text-foreground">
                  {locale === 'ar' ? 'وسيلة النقل' : 'Transportation'}
                </h4>
              </div>
              <div className="p-4">
                <div className="bg-background/50 px-4 py-3 rounded-lg border border-border/40">
                  <span className="font-medium text-foreground text-base">
                    {(() => {
                      const value = preparedPayload?.transportation;
                      if (!value) return '—';
                      if (locale === 'ar') {
                        if (value === 'Bus') return 'بواسطة الحافلة';
                        if (value === 'Car') return 'بالسيارة';
                        if (value === 'On Foot') return 'سيراً على الأقدام';
                      }
                      return value === 'Bus'
                        ? 'By Bus'
                        : value === 'Car'
                          ? 'By Car'
                          : value === 'On Foot'
                            ? 'On Foot'
                            : value;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {preparedPayload?.transportation !== 'Bus' && (
              <div className="rounded-lg border-2 border-primary/30 bg-card/30 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-primary/5 to-transparent border-b border-border/40">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <svg
                      className="w-5 h-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9a2 2 0 012-2h2"
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12v9" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16h.01M16 16h.01" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8l5-5 5 5" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-base text-foreground">
                    {locale === 'ar' ? 'تطبيق على جميع أبنائي' : 'Apply to all my children'}
                  </h4>
                </div>
                <div className="p-4 space-y-3">
                  <label
                    className={clsx(
                      'flex items-start gap-3 text-sm font-medium text-foreground cursor-pointer',
                      locale === 'ar' && 'flex-row text-right'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={applyToAllChildren}
                      onChange={(event) => {
                        const { checked } = event.target;
                        setApplyToAllChildren(checked);
                        setSelectedChildIds([]);
                        setChildFetchError(null);
                        if (checked) {
                          if (eligibleChildren.length === 0 && !isFetchingChildren) {
                            void fetchEligibleChildren();
                          }
                        }
                      }}
                      className="mt-1 h-4 w-4 rounded border-primary/40 text-primary focus:ring-primary"
                    />
                    <div className="space-y-1">
                      <span>
                        {locale === 'ar'
                          ? 'استخدام نفس البيانات لجميع الأبناء المرتبطين بحسابي'
                          : 'Use the same data for all children linked to my account'}
                      </span>
                      {!applyToAllChildren && (
                        <p className="text-xs text-muted-foreground">
                          {locale === 'ar'
                            ? 'عند تحديد هذا الخيار، سيتم استخدام نفس البيانات المذكورة أعلاه لجميع الأبناء المرتبطين بحسابك.'
                            : 'When this option is selected, all children linked to your account will use the same data as above.'}
                        </p>
                      )}
                    </div>
                  </label>

                  {applyToAllChildren && (
                    <div
                      className={clsx(
                        'space-y-2 rounded-lg border border-border/40 bg-background/50 p-3',
                        locale === 'ar' && 'text-right'
                      )}
                    >
                      {isFetchingChildren && (
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>
                            {locale === 'ar'
                              ? 'جارٍ التحقق من أهلية الأطفال...'
                              : 'Checking which children are eligible...'}
                          </span>
                        </div>
                      )}

                      {!isFetchingChildren && childFetchError && (
                        <p className="text-sm text-destructive">{childFetchError}</p>
                      )}

                      {!isFetchingChildren && !childFetchError && eligibleChildren.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          {locale === 'ar'
                            ? 'لا يوجد أطفال مستوفون للشروط حاليًا'
                            : 'No children are currently eligible.'}
                        </p>
                      )}

                      {!isFetchingChildren && !childFetchError && eligibleChildren.length > 0 && (
                        <>
                          {eligibleChildren.map((entry) => {
                            const childId = textOrNull(entry.profile.id) ?? entry.profile.id;
                            if (!childId) return null;
                            const name = getDisplayName(entry.profile, locale);
                            const details: string[] = [];
                            if (entry.profile.studentNumber) {
                              details.push(
                                locale === 'ar'
                                  ? `رقم الطالب: ${entry.profile.studentNumber}`
                                  : `Student #: ${entry.profile.studentNumber}`
                              );
                            }
                            if (entry.educationType) {
                              details.push(
                                locale === 'ar'
                                  ? `نوع التعليم: ${entry.educationType}`
                                  : `Education: ${entry.educationType}`
                              );
                            }
                            const isChecked = selectedChildIds.includes(childId);
                            return (
                              <label
                                key={childId}
                                className={clsx(
                                  'flex items-start gap-3 rounded-md border border-border/30 bg-card/40 px-3 py-2 transition-colors hover:border-primary/40',
                                  locale === 'ar' && 'flex-row text-right'
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(event) => handleChildSelectionChange(childId, event.target.checked)}
                                  className="mt-1 h-4 w-4 rounded border-primary/40 text-primary focus:ring-primary"
                                />
                                <div className="flex-1 space-y-1">
                                  <p className="font-medium text-sm text-foreground">{name}</p>
                                  {details.length > 0 && (
                                    <p className="text-[10px] text-muted-foreground">{details.join(' | ')}</p>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                          {selectedChildIds.length === 0 && (
                            <p className="text-xs text-destructive">
                              {locale === 'ar'
                                ? 'يرجى اختيار طفل واحد على الأقل للتطبيق.'
                                : 'Select at least one child to continue.'}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Warning Message */}
            <div className="rounded-lg border-2 border-amber-500/40 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 p-5">
              <div className={clsx('flex gap-4', locale === 'ar' && 'flex-row')}>
                <svg
                  className="w-6 h-6 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p
                  className={clsx(
                    'text-sm text-amber-900 dark:text-amber-100 leading-relaxed font-medium',
                    locale === 'ar' && 'text-right'
                  )}
                >
                  {locale === 'ar'
                    ? 'بالنقر على "تأكيد وإرسال"، أؤكد أن جميع المعلومات أعلاه صحيحة وكاملة. في حال عدم وجود أي تغييرات، سيتم اعتماد البيانات تلقائيًا؛ أما في حال وجود تغييرات، فسيتم إرسال طلب تحديث إلى المدرسة لمراجعته واعتماده.'
                    : 'By clicking "Confirm & Submit", I confirm that all the information above is accurate and complete. If no changes are made, the information will be approved automatically; otherwise, an update request will be sent to the school for review and approval.'}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3 pt-4 border-t border-border/30">
            <button
              type="button"
              onClick={() => setShowConfirmDialog(false)}
              className="aegov-btn btn-soft btn-secondary btn-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {locale === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              disabled={isSubmitting || (applyToAllChildren && selectedChildIds.length === 0)}
              className="aegov-btn btn-sm"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {locale === 'ar' ? 'جارٍ الحفظ...' : 'Saving...'}
                </>
              ) : (
                <>
                  <CheckCircleIcon />
                  {locale === 'ar' ? 'تأكيد والإرسال' : 'Confirm & Submit'}
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
